import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AOS from 'aos';
import 'aos/dist/aos.css';

// Components Imports
import Navbar from './components/Navbar';
import ImageCarousel from './components/ImageCarousel';
import CategorySection from './components/CategorySection';
import ExploreClasses from './components/ExploreClasses'; 
import SuccessStories from './components/SuccessStories'; 
import TeacherDirectory from './components/TeacherDirectory';
import WhyChooseUs from './components/WhyChooseUs';
import TeacherSection from './components/TeacherSection';
import PortalLogin from './components/PortalLogin'; 
import GalleryModal from './components/GalleryModal';

import ContactSection from './components/ContactSection';
import SmartClassBanner from './components/SmartClassBanner';
import EventsSection from './components/EventsSection';
import Footer from './components/Footer';
import Preloader from './components/Preloader';
import BackToTop from './components/BackToTop';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  
  // States for Modals/Sections
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFaculty, setShowFaculty] = useState(false);
  const [showPortal, setShowPortal] = useState(false);
  const [showGallery, setShowGallery] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2500);

    AOS.init({
      duration: 1000,
      once: true,
      easing: 'ease-out-quad',
    });

    return () => clearTimeout(timer);
  }, []);

  // --- Logic Handlers ---

  const handleCategorySelect = (categoryName) => {
    setShowSuccess(false);
    setShowFaculty(false);
    setSelectedCategory(categoryName);
    
    setTimeout(() => {
      const element = document.getElementById('explore-section-anchor');
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  const handleExploreSuccess = () => {
    setSelectedCategory(null);
    setShowFaculty(false);
    setShowSuccess(true);
  };

  const handleShowFaculty = () => {
    setSelectedCategory(null);
    setShowSuccess(false);
    setShowFaculty(true);
  };

  // 1. Full Screen Portal View Logic
  if (!isLoading && showPortal) {
    return (
      <AnimatePresence>
        <PortalLogin onBack={() => setShowPortal(false)} />
      </AnimatePresence>
    );
  }

  return (
    <>
      {/* PRELOADER */}
      <AnimatePresence mode="wait">
        {isLoading && <Preloader key="loader" />}
      </AnimatePresence>

      {!isLoading && (
        <div className="min-h-screen bg-[#020617] font-sans overflow-x-hidden">
          
          {/* NAVIGATION */}
          <Navbar onPortalClick={() => setShowPortal(true)} />
          
          {/* TOP SPACING */}
          <div className="h-20 md:h-24"></div>

          {/* --- POSH HERO SECTION --- */}
          <header id="home" className="relative min-h-[90vh] flex items-center overflow-hidden px-6 md:px-12 py-12">
            
            {/* Background Tech Glows */}
            <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-indigo-600/10 rounded-full blur-[150px] -mr-40 -mt-40"></div>
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-red-600/5 rounded-full blur-[120px] -ml-20 -mb-20"></div>

            <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
              
              {/* LEFT: TEXT CONTENT */}
              <div className="text-center lg:text-left space-y-8" data-aos="fade-right">
                <div className="flex items-center justify-center lg:justify-start gap-3">
                  <span className="w-12 h-[2px] bg-red-600"></span>
                  <span className="text-red-600 font-black uppercase tracking-[0.4em] text-[10px]">Premium Education Ecosystem</span>
                </div>

                {/* SINHALA HEADING WITH MAX WEIGHT */}
                <h1 className="text-4xl md:text-[5.5rem] font-[900] text-white leading-[1.1] tracking-tighter">
                  ජීවිතය දිනන්න <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-orange-500 to-yellow-400 drop-shadow-[0_10px_10px_rgba(220,38,38,0.2)]">
                     අදම 
                     ඔබේ අසුන වෙන්කරවා ගන්න
                  </span>
                </h1>

                <p className="text-slate-400 text-lg md:text-xl max-w-xl font-medium leading-relaxed">
                  Join Matale's premier educational institute. Unlock your potential with our world-class faculty and cutting-edge digital learning tools.
                </p>

                <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-6 pt-4">
                  <a 
                    href="https://portal.vidulaka.lk/register" 
                    target="_blank" rel="noreferrer"
                    className="group relative px-10 py-5 bg-red-600 text-white font-black uppercase tracking-widest text-xs rounded-xl overflow-hidden transition-all hover:bg-red-700 active:scale-95 shadow-2xl shadow-red-900/30"
                  >
                    <span className="relative z-10">Register Now</span>
                    <div className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500"></div>
                  </a>

                  <button 
                    onClick={() => handleCategorySelect('All')}
                    className="flex items-center gap-4 text-white text-[11px] font-black uppercase tracking-[0.3em] group hover:text-red-500 transition-colors"
                  >
                    <span className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center group-hover:border-red-600 transition-all duration-300">
                      <span className="group-hover:translate-x-1 transition-transform italic text-lg text-red-600 font-black">→</span>
                    </span>
                    View Classes
                  </button>
                </div>
              </div>

              {/* RIGHT: IMAGE CAROUSEL AREA */}
              <div className="relative" data-aos="fade-left">
                {/* Decorative Frames */}
                <div className="absolute -inset-4 border border-white/5 rounded-[2.5rem] md:rounded-[4.5rem] pointer-events-none"></div>
                <div className="absolute -inset-8 border border-white/5 rounded-[3.5rem] md:rounded-[5.5rem] opacity-30 pointer-events-none"></div>
                
                {/* The Carousel */}
                <div className="relative z-10 rounded-[2rem] md:rounded-[3.5rem] overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)] border border-white/10 bg-slate-900">
                   <ImageCarousel /> 
                </div>

                {/* Floating Badge */}
                
              </div>

            </div>
          </header>

          {/* --- MAIN SECTIONS --- */}
          
          <div data-aos="fade-up">
            <CategorySection onCategoryClick={handleCategorySelect} />
          </div>

          <AnimatePresence>
            {selectedCategory && (
              <motion.div 
                id="explore-section-anchor"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-slate-900/50"
              >
                <ExploreClasses 
                  activeCategory={selectedCategory} 
                  onClose={() => setSelectedCategory(null)} 
                />
              </motion.div>
            )}
          </AnimatePresence>

          <div data-aos="fade-up"><WhyChooseUs onExploreSuccess={handleExploreSuccess} /></div>
          <div data-aos="fade-up"><TeacherSection onShowAllTeachers={handleShowFaculty} /></div>
          
          {/* Events Section with Gallery Modal Trigger */}
          <div data-aos="fade-up">
            <EventsSection onExploreGallery={() => setShowGallery(true)} />
          </div>

          {/* Interaction & Contact */}
          <div id="contact" data-aos="fade-up"><ContactSection /></div>
          <div data-aos="zoom-in" className="py-10"><SmartClassBanner /></div>

          {/* FOOTER AREA */}
          <Footer />
          <BackToTop />

          {/* --- MODALS (AnimatePresence) --- */}
          <AnimatePresence>
            {showSuccess && <SuccessStories onClose={() => setShowSuccess(false)} />}
            {showFaculty && <TeacherDirectory onClose={() => setShowFaculty(false)} />}
            {showGallery && <GalleryModal onClose={() => setShowGallery(false)} />}
          </AnimatePresence>

        </div>
      )}
    </>
  );
}

export default App;